import React, { useEffect, useRef, useState } from "react";
import { IconButton, CloseIcon } from "native-base";
import { CalendarIcon } from "@mui/x-date-pickers";
import { Loc } from "../../App/Loc";
import Moment from "moment";
import { SearchIcon } from "../../assets/icons/icon-assets";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import { Person } from "../../AVCore/Person";
import * as AV from "../../AVCore/AVCore";
import { AVCalendarView } from "../../components/common/calendar/AVCalendarView";
import { EventsCardLoader } from "../../components/loaders";
import useFetchAllEvents from "../../Hooks/useFetchAllEvents";
import {
  Box,
  Button,
  HStack,
  Image,
  Input,
  Pressable,
  Text,
  Tooltip,
  VStack,
} from "native-base";
import { Dimensions } from "react-native";
import { useNavigate } from "react-router-dom";
import RenderFilters from "./RenderFilters";
import EventCard from "./EventCard";

const EventsContent = ({ mainPage = true, user, ownerId = null }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const { events, loading, error } = useFetchAllEvents(user, ownerId);

  const dimensions = Dimensions.get("window");
  const isMobile = dimensions?.width <= 500 || false;
  const [isActives, setIsActives] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [allEventData, setEventData] = useState<any>([events]);
  const [allEvents, setAllEvents] = useState<any>([events]);
  const [filteredEvents, setFilteredEvents] = useState<any>([]);
  const [searchedEvents, setSearchedEvents] = useState<any>([]);
  const [eventType, setEventType] = useState(Loc.currentLang.menu.current);
  const [isCalendarView, setIsCalenderView] = useState(true);
  const [categories, setCategories] = useState<any>([]);
  const [selectedFilter, setSelectedFilter] = useState("Current");
  const [userName, setUserName] = useState("");
  const menuRefs = useRef(null);

  useOutsideAlerter(menuRefs);

  function useOutsideAlerter(ref) {
    useEffect(() => {
      let isMounted = true;
      if (isMounted) {
        function handleClickOutside(event) {
          if (ref.current && !ref.current.contains(event.target)) {
            setIsActives(false);
          }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }
      return () => {
        isMounted = false;
      };
    }, [ref]);
  }

  useEffect(() => {
    const calendarEvents = events?.filter((ev) => !isCanceled(ev));
    setEventData(calendarEvents);
    setAllEvents(events);

    const currentEvents = events?.filter(
      (ev) => !isCanceled(ev) && new Date(ev.end) >= new Date(),
    );

    setFilteredEvents(currentEvents);
    setSelectedFilter("Current");

    const uniqueCategories = [
      "Current",
      ...new Set(currentEvents.map((event) => event.linkType)),
    ];
    const past = events?.filter(
      (ev) => new Date(ev.end) < new Date() && !isCanceled(ev),
    );
    const canceled = events?.filter((ev) => isCanceled(ev));
    if (past?.length > 0) uniqueCategories.push("Past");
    if (canceled?.length > 0) uniqueCategories.push("Canceled");

    setCategories(uniqueCategories.filter(Boolean)); // Remove any undefined/null values
  }, [events, loading]);

  useEffect(() => {
    let mounted = true;

    Person.getSelf()
      .then((user) => {
        if (mounted && user?.name) {
          setUserName(user.name);
        }
      })
      .catch(console.error);

    return () => {
      mounted = false;
    };
  }, []);

  const isCanceled = (event: any) =>
    event?.data?.toLowerCase() === "canceled" ||
    event?.status?.toLowerCase() === "canceled";

  const handleFilters = (filter) => {
    try {
      setSelectedFilter(filter);
      setSearchValue("");
      setFilteredEvents([]);

      const now = new Date();
      let filteredEvents = [];

      switch (filter) {
        case "Past":
          filteredEvents = events.filter(
            (event) => new Date(event.end) < now && !isCanceled(event),
          );
          break;
        case "Canceled":
          filteredEvents = events.filter((event) => isCanceled(event));
          break;
        case "Current":
          filteredEvents = events?.filter(
            (event) => !isCanceled(event) && new Date(event.end) >= now,
          );
          break;
        default:
          filteredEvents = events.filter(
            (event) =>
              event.linkType === filter &&
              !isCanceled(event) &&
              new Date(event.end) >= now,
          );
          break;
      }

      setFilteredEvents(filteredEvents);
    } catch (e) {
      console.error("Error in filtering the results", e);
    }
  };

  const handleSearchEvents = async (item) => {
    setSearchValue(item);
    if (item) {
      let searchData = filteredEvents?.filter((x) => {
        const titleIncludesItem = x?.title
          ?.toLowerCase()
          ?.includes(item.toLowerCase());

        if (selectedFilter?.toLowerCase() === "current") {
          return (
            new Date(x.end) >= new Date() && !isCanceled(x) && titleIncludesItem
          );
        } else if (selectedFilter?.toLowerCase() === "past") {
          return (
            new Date(x.end) < new Date() && !isCanceled(x) && titleIncludesItem
          );
        } else if (selectedFilter?.toLowerCase() === "canceled") {
          return isCanceled(x) && titleIncludesItem;
        } else {
          return (
            x.linkType?.toLowerCase() === selectedFilter?.toLowerCase() &&
            !isCanceled(x) &&
            new Date(x.end) >= new Date() &&
            titleIncludesItem
          );
        }
      });

      setSearchedEvents(searchData);
      // setFilteredEvents(searchData);
    } else {
      setSearchValue("");
      setSearchedEvents([]);
    }
  };

  const handleEventClick = (item) => {
    try {
      console.log("handlenavigate", item);
      navigate(`/Events/${item?.id}/${item?.region}/${item?.seriesId}`, {
        state: { team: item, seriesId: item?.seriesId },
      });
    } catch (e) {
      console.log("Error in opening the Event", e);
    }
  };

  const eventsToRender =
    searchValue.trim() !== "" ? searchedEvents : filteredEvents;

  return (
    <>
      <Box
        bgColor="#fff"
        shadow="1"
        borderRadius={5}
        height={isMobile ? "160px" : ""}
      >
        <VStack px={3} pt={2}>
          <HStack
            alignItems={"center"}
            space={3}
            flex={1}
            justifyContent={"space-between"}
          >
            {!isMobile && (
              <Text
                fontSize={"20px"}
                fontWeight={"bold"}
                width={isMobile ? "25%" : "40%"}
                text-overflow={"ellipsis"}
                numberOfLines={1}
              >
                {Loc.currentLang.menu.hello},
                <Text
                  color={"AVColor.primary"}
                  width={"100px"}
                  text-overflow={"ellipsis"}
                  fontSize={"17px"}
                  numberOfLines={1}
                >
                  {userName}!
                </Text>
              </Text>
            )}
            <HStack
              alignItems={"center"}
              justifyContent={isMobile ? "flex-start" : "flex-end"}
              space={2}
              width={isMobile ? "100%" : "61%"}
            >
              {!isMobile && (
                <Text fontSize={[12, 14]} color="AVColor.textLite">
                  {Moment().format("MMMM D, Y")}
                </Text>
              )}

              {!isCalendarView && (
                <Input
                  width={isMobile ? "97%" : "251px"}
                  borderWidth={0}
                  placeholder={Loc.currentLang.menu.searchEvents}
                  variant="AVSearchInput"
                  placeholderTextColor="AVColor.textLite"
                  backgroundColor={"#F5F5F5"}
                  borderRadius={"22px"}
                  fontSize={"16px"}
                  value={searchValue}
                  InputLeftElement={
                    <SearchIcon
                      ml="2"
                      size="24px"
                      color="#000"
                      left={"10px"}
                      p={"7px"}
                    />
                  }
                  InputRightElement={
                    searchValue ? (
                      <IconButton
                        icon={<CloseIcon size="xs" />}
                        size="sm"
                        variant="AVSearchInput"
                        onPress={() => {
                          setSearchValue("");
                          handleSearchEvents("");
                        }}
                        _hover={{ bg: "transparent" }}
                      />
                    ) : undefined
                  }
                  onChangeText={(e) => handleSearchEvents(e)}
                />
              )}
            </HStack>
          </HStack>

          {/* menu */}
          <div className="custom-scrollbar">
            <HStack width={"100%"}>
              <HStack
                marginY={"24px"}
                overflowX={isMobile ? "scroll" : undefined}
                backgroundColor={"rgba(0,0,0,0.05)"}
                borderRadius={"18px"}
                width={isMobile ? "322px" : "auto"}
                right={isMobile ? "10px" : ""}
                height={"36px"}
              >
                {!isCalendarView && mainPage && (
                  <>
                    {categories?.length > 0 && (
                      <RenderFilters
                        categories={categories}
                        selectedFilter={selectedFilter}
                        handleFilters={handleFilters}
                      />
                    )}
                  </>
                )}
              </HStack>

              {allEvents?.length > 0 && (
                <HStack
                  position={"absolute"}
                  ref={menuRefs}
                  right={0}
                  top={isMobile ? "68px" : "30px"}
                  zIndex={999}
                  alignItems={"center"}
                  space={1}
                >
                  {isCalendarView && (
                    <FormatListBulletedIcon
                      style={{ color: "#00A7BC" }}
                      onClick={() => setIsCalenderView(false)}
                      cursor={"pointer"}
                    />
                  )}
                  {!isCalendarView && (
                    <CalendarIcon
                      style={{ color: "#00A7BC" }}
                      onClick={() => {
                        setSearchValue("");
                        setIsCalenderView(true);
                      }}
                      cursor={"pointer"}
                    />
                  )}
                </HStack>
              )}
            </HStack>
          </div>
        </VStack>
      </Box>
      <VStack my={3} zIndex={-3}>
        <HStack>
          <Text fontSize={"24px"} color={"AVColor.primary"}>
            {Loc.currentLang.menu.events}
          </Text>
        </HStack>

        <HStack
          backgroundColor={"AVColor.white"}
          space={2}
          fontWeight={"500"}
          flexWrap={"wrap"}
          height={"auto"}
          padding={"8px"}
          paddingLeft={"10px"}
          borderRadius={10}
          width={"100%"}
          alignItems={"center"}
          justifyContent={"center"}
          marginTop={allEvents?.length === 0 ? "10px" : "8px"}
        >
          {loading && (
            <HStack
              width={"100%"}
              flexWrap={"wrap"}
              justifyContent={"space-around"}
            >
              {[...Array(9)].map((i, index) => (
                <Box key={index} width={"1/3"}>
                  <EventsCardLoader />
                </Box>
              ))}
            </HStack>
          )}

          {isCalendarView && (
            <AVCalendarView
              data={allEventData?.filter((x) => x.status != "No")}
              eventType={Loc.currentLang.menu.current}
            />
          )}

          {!isCalendarView &&
            eventsToRender?.length > 0 &&
            [...eventsToRender]?.map((item, index) => {
              return (
                <Box key={index}>
                  <EventCard item={item} handleEventClick={handleEventClick} />
                </Box>
              );
            })}

          {((searchValue.trim() != "" && searchedEvents?.length == 0) ||
            filteredEvents?.length == 0) &&
            !isCalendarView && (
              <VStack
                width={"100%"}
                justifyContent={"center"}
                alignItems={"center"}
              >
                <HStack padding={"10px"} alignItems={"center"} zIndex={-3}>
                  <Text>{Loc.currentLang.menu.noEventsFound}</Text>
                </HStack>
              </VStack>
            )}
        </HStack>
      </VStack>
    </>
  );
};

export default EventsContent;
